'use client';

import { c as _c } from "react/compiler-runtime";
import { getCookies } from 'cookies-next/client';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { maxAge, minAge, vulnerableCountryIsoCodes } from '@sola/contentful-model/lib/sac-values.ts';
import { childSearch, getRandomChildren } from '@sola/proxy-sdk/children.ts';
import type { ChildAvailabilityItem } from '@sola/schema/dotcom/children/child.ts';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.ts';
import type { SacFilterProps } from '@sola/schema/lib/schema.ts';
import type { SacQueryParams } from '../../utils/query-params.sac.tsx';
import { SacFiltersEnum } from '../../utils/query-params.sac.tsx';
const defaultLimit = 12;
const maxPages = 9;
const SAC_SEED_KEY = 'sacRichMixSeed';
/** Max value for a 32-bit signed integer — safe upper bound for backend seed params. */
const maxInt32 = 2_147_483_647;

/** Get or create a stable integer seed for the current user. */
const getOrCreateSeed = (): number => {
  try {
    const stored = localStorage.getItem(SAC_SEED_KEY);
    if (stored) {
      const {
        seed
      } = JSON.parse(stored);
      if (typeof seed === 'number') {
        return seed;
      }
    }
  } catch {
    // Malformed or inaccessible localStorage — generate fresh
  }
  const seed = Math.floor(Math.random() * maxInt32);
  try {
    localStorage.setItem(SAC_SEED_KEY, JSON.stringify({
      seed
    }));
  } catch {
    // Storage full or unavailable — seed still works for this session
  }
  return seed;
};

/** Narrow countries to HVC overlap when the vulnerable filter is active. */
export const resolveFilteredCountries = (countries: string[] | null, isVulnerable: boolean | undefined): string[] | undefined => {
  if (!isVulnerable) {
    return countries ? [...countries] : undefined;
  }
  const hvc = vulnerableCountryIsoCodes as readonly string[];
  const selected = countries ? [...countries] : undefined;
  if (selected?.length) {
    const matched = selected.filter(c => hvc.includes(c));
    return matched.length > 0 ? matched : selected;
  }
  return [...vulnerableCountryIsoCodes];
};

/** Normalize gender — treat "Either" as unfiltered. */
export const normalizeGender = (gender?: string | null): string | null => gender && gender !== ChildGenderEnum.Either ? gender : null;

/** Map chip filter strings → boolean flags. */
export const resolveChipFilters = (filters?: string[]) => ({
  disability: filters?.includes(SacFiltersEnum.enum.hasDisabilities) || undefined,
  isInHivAffectedArea: filters?.includes(SacFiltersEnum.enum.affectedHiv) || undefined,
  isOrphan: filters?.includes(SacFiltersEnum.enum.orphanedChild) || undefined,
  isVulnerable: filters?.includes(SacFiltersEnum.enum.vulnerableExploitation) || undefined
});
export type ResolvedSacFilters = {
  gender: string | null;
  ageMin: number | null;
  ageMax: number | null;
  countries: string[] | null;
  birthDay: string | null;
  birthMonth: string | null;
  disability: boolean | undefined;
  isInHivAffectedArea: boolean | undefined;
  isOrphan: boolean | undefined;
  isVulnerable: boolean | undefined;
  limit: number;
};

/** True when any filter differs from the default/empty state. */
export const hasCriteria = (f: ResolvedSacFilters): boolean => !!(f.gender && f.gender !== ChildGenderEnum.Either || (f.ageMin && f.ageMax ? f.ageMin !== minAge || f.ageMax !== maxAge : f.ageMin || f.ageMax) || (f.countries?.length ?? 0) > 0 || f.birthDay || f.birthMonth || f.isInHivAffectedArea || f.disability || f.isOrphan || f.isVulnerable);

/** Map CMS props → ResolvedSacFilters (non-live embeds). */
export const resolveFiltersFromProps = (filters: SacFilterProps): ResolvedSacFilters => ({
  gender: normalizeGender(filters.gender),
  ageMin: filters.ageMin ?? null,
  ageMax: filters.ageMax ?? null,
  countries: filters.countries ?? null,
  birthDay: null,
  birthMonth: null,
  disability: filters.disability || undefined,
  isInHivAffectedArea: filters.hiv || undefined,
  isOrphan: filters.orphan || undefined,
  isVulnerable: filters.highlyVulnerable || undefined,
  limit: filters.limit ?? defaultLimit
});

/** Map URL query params → ResolvedSacFilters (live SAC page). */
export const resolveFiltersFromQueryParams = (sacFilters: SacQueryParams, limit = defaultLimit): ResolvedSacFilters => ({
  gender: normalizeGender(sacFilters.gender),
  ageMin: sacFilters.ageMin ?? null,
  ageMax: sacFilters.ageMax ?? null,
  countries: sacFilters.countries?.filter(Boolean) ?? null,
  birthDay: sacFilters.birthDay ? String(sacFilters.birthDay) : null,
  birthMonth: sacFilters.birthMonth ? String(sacFilters.birthMonth) : null,
  ...resolveChipFilters(sacFilters.filters),
  limit
});
export const useSacList = (resolved, isDynamic) => {
  const $ = _c(56);
  const {
    gender,
    ageMin,
    ageMax,
    countries,
    birthDay,
    birthMonth,
    disability,
    isInHivAffectedArea,
    isOrphan,
    isVulnerable,
    limit
  } = resolved;
  let t0;
  if ($[0] !== countries || $[1] !== isVulnerable) {
    t0 = resolveFilteredCountries(countries, isVulnerable);
    $[0] = countries;
    $[1] = isVulnerable;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const filteredCountries = t0;
  let t1;
  if ($[3] !== resolved) {
    t1 = hasCriteria(resolved);
    $[3] = resolved;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const hasActiveCriteria = t1;
  const [page, setPage] = useState(0);
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const [storedChildren, setStoredChildren] = useState(t2);
  const [isLoading, setIsLoading] = useState(true);
  const pathname = usePathname();
  const router = useRouter();
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = getCookies();
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const cookieStore = t3;
  const params = useSearchParams();
  let t4;
  if ($[7] !== pathname || $[8] !== router) {
    t4 = () => router.push(pathname);
    $[7] = pathname;
    $[8] = router;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const resetFilters = t4;
  let t5;
  if ($[10] !== limit || $[11] !== page || $[12] !== storedChildren) {
    t5 = storedChildren.slice(0, (page + 1) * limit);
    $[10] = limit;
    $[11] = page;
    $[12] = storedChildren;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const children = t5;
  const canLoadMore = storedChildren.length > 0 && page < maxPages && storedChildren.length > (page + 1) * limit;
  let t6;
  if ($[14] !== params) {
    t6 = params.toString();
    $[14] = params;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  const sacParamsKey = t6;
  let t7;
  if ($[16] !== page) {
    t7 = () => {
      if (page < maxPages) {
        setPage(page + 1);
      }
    };
    $[16] = page;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  const incrementPage = t7;
  let t8;
  if ($[18] !== ageMax || $[19] !== ageMin || $[20] !== birthDay || $[21] !== birthMonth || $[22] !== countries || $[23] !== disability || $[24] !== filteredCountries || $[25] !== gender || $[26] !== hasActiveCriteria || $[27] !== isInHivAffectedArea || $[28] !== isOrphan || $[29] !== isVulnerable || $[30] !== limit) {
    t8 = () => {
      let cancelled = false;
      const referrer = String(cookieStore?.referrer);
      const loadChildren = async () => {
        setStoredChildren([]);
        setIsLoading(true);
        setPage(0);
        const seed = getOrCreateSeed();
        const results = hasActiveCriteria ? await childSearch({
          take: limit * 10,
          skip: 0,
          gender: gender as ChildGenderEnum,
          ageMin,
          ageMax,
          countries: isVulnerable ? filteredCountries : countries,
          birthDay,
          birthMonth,
          isOrphan,
          isInHivAffectedArea,
          disability,
          referrer
        }) : await getRandomChildren(limit * 10, seed);
        if (cancelled) {
          return;
        }
        setStoredChildren(results?.children as ChildAvailabilityItem[] || []);
        setPage(0);
        setIsLoading(false);
      };
      loadChildren();
      return () => {
        cancelled = true;
      };
    };
    $[18] = ageMax;
    $[19] = ageMin;
    $[20] = birthDay;
    $[21] = birthMonth;
    $[22] = countries;
    $[23] = disability;
    $[24] = filteredCountries;
    $[25] = gender;
    $[26] = hasActiveCriteria;
    $[27] = isInHivAffectedArea;
    $[28] = isOrphan;
    $[29] = isVulnerable;
    $[30] = limit;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== ageMax || $[33] !== ageMin || $[34] !== birthDay || $[35] !== birthMonth || $[36] !== countries || $[37] !== disability || $[38] !== filteredCountries || $[39] !== gender || $[40] !== hasActiveCriteria || $[41] !== isInHivAffectedArea || $[42] !== isOrphan || $[43] !== isVulnerable || $[44] !== limit || $[45] !== sacParamsKey) {
    t9 = [gender, ageMax, ageMin, countries, filteredCountries, disability, isInHivAffectedArea, isOrphan, isVulnerable, birthDay, birthMonth, limit, sacParamsKey, hasActiveCriteria];
    $[32] = ageMax;
    $[33] = ageMin;
    $[34] = birthDay;
    $[35] = birthMonth;
    $[36] = countries;
    $[37] = disability;
    $[38] = filteredCountries;
    $[39] = gender;
    $[40] = hasActiveCriteria;
    $[41] = isInHivAffectedArea;
    $[42] = isOrphan;
    $[43] = isVulnerable;
    $[44] = limit;
    $[45] = sacParamsKey;
    $[46] = t9;
  } else {
    t9 = $[46];
  }
  useEffect(t8, t9);
  let t10;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[47] = t10;
  } else {
    t10 = $[47];
  }
  useEffect(_temp2, t10);
  const t11 = !hasActiveCriteria;
  const t12 = isDynamic && canLoadMore;
  let t13;
  if ($[48] !== children || $[49] !== incrementPage || $[50] !== isLoading || $[51] !== limit || $[52] !== resetFilters || $[53] !== t11 || $[54] !== t12) {
    t13 = {
      isLoading,
      children,
      showRichMix: t11,
      showLoadMoreButton: t12,
      incrementPage,
      resetFilters,
      limit
    };
    $[48] = children;
    $[49] = incrementPage;
    $[50] = isLoading;
    $[51] = limit;
    $[52] = resetFilters;
    $[53] = t11;
    $[54] = t12;
    $[55] = t13;
  } else {
    t13 = $[55];
  }
  return t13;
};
function _temp() {
  if (globalThis.history?.scrollRestoration) {
    globalThis.history.scrollRestoration = "auto";
  }
}
function _temp2() {
  if (globalThis.history?.scrollRestoration) {
    globalThis.history.scrollRestoration = "manual";
  }
  return _temp;
}